import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FOCUSRING } from '../focus';

export interface ScrollableTableProps {
  /** De naam van de tabel, zoals hij ook in het bijschrift staat. */
  naam: string;
  children: ReactNode;
}

/**
 * De plakkende eerste kolom, in twee smaken (UX-DR21).
 *
 * Tussen 768 en 1279px schuiven de cijfers horizontaal langs; blijft de naam of het veld dan niet
 * staan, dan weet de lezer halverwege niet meer bij wie een getal hoort. `left-0` plakt hem vast,
 * `z-*` houdt hem bóven de cellen die eronderdoor schuiven, en de eigen achtergrond is geen
 * versiering maar de reden dat het werkt: zonder vlak schuift de tekst van de tweede kolom er
 * dwars doorheen.
 *
 * De kopcel plakt een laag hoger dan de rijkop, zodat hij bij het horizontaal schuiven boven de
 * cellen van zijn eigen rij blijft. Verticaal plakt er niets: deze module kent geen `sticky top-0`.
 */
// De randen van een tabel met `border-collapse: collapse` worden door de tabel getekend en niet
// door de cel, dus zij schuiven onder een plakkende kolom weg. De schaduw van één pixel tekent die
// naad opnieuw op de cel zelf, zodat de kolom een rand houdt terwijl de rest langsschuift.
// `group-hover` houdt de plakkende cel mee-oplichten met haar rij; zonder dat blijft precies de
// kolom die de lezer volgt wit terwijl de rest van de rij kleurt.
export const PLAKKENDE_KOP =
  'sticky left-0 z-20 bg-[var(--background-muted)] shadow-[1px_0_0_var(--border-soft)]';
export const PLAKKENDE_RIJKOP =
  'sticky left-0 z-10 bg-[var(--background)] shadow-[1px_0_0_var(--border-soft)] group-hover:bg-[var(--background-muted)]';

/**
 * Een tabel met haar eigen horizontale scroll — bereikbaar met het toetsenbord.
 *
 * `DESIGN.md` geeft elke brede tabel een eigen `overflow-x: auto`, zodat de pagina zelf nooit
 * horizontaal scrollt. Een scrollbaar gebied dat geen focus kan krijgen is echter met alleen een
 * toetsenbord niet te verschuiven — in Safari blijven de weggeschoven kolommen dan onbereikbaar
 * (WCAG 2.1.1). Vandaar `tabIndex={0}` met een `role="region"` en een naam: het gebied krijgt focus,
 * kondigt zich aan, en de focusring van het huis maakt zichtbaar waar de lezer staat.
 */
export function ScrollableTable({ naam, children }: ScrollableTableProps) {
  const { t } = useTranslation();

  return (
    <div
      aria-label={t('qa.alle.tabelScrollLabel', { naam })}
      className={`overflow-x-auto rounded-[4px] ${FOCUSRING}`}
      data-werkkwaliteit-scroll="tabel"
      role="region"
      tabIndex={0}
    >
      {children}
    </div>
  );
}
